import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { fetchCourses } from '@/services/courses';
import { fetchLessonsByCourseId } from '@/services/lessons';
import { organizationService } from '@/services/api';
import { fetchDashboardStats } from '@/services/stats';

export interface DashboardStats {
  totalCourses: number;
  publishedCourses: number;
  draftCourses: number;
  totalUsers: number;
  activeUsers: number;
  completedCourses: number;
  totalOrganizations: number;
}

export interface CourseData {
  id: string;
  title: string;
  description: string | null;
  status: 'published' | 'draft';
  lesson_count: number;
  created_at_formatted: string;
  updated_at_formatted: string;
  created_at: string;
  updated_at: string;
}

export interface UserOrganization {
  id: string;
  role: string;
  organizations: {
    id: string;
    name: string;
    description: string | null;
  };
}

export const useDashboardData = () => {
  const { toast } = useToast();
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<CourseData[]>([]);
  const [showDrafts, setShowDrafts] = useState(true);
  const [showPublished, setShowPublished] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    publishedCourses: 0,
    draftCourses: 0,
    totalUsers: 0,
    activeUsers: 0,
    completedCourses: 0,
    totalOrganizations: 0
  });
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
  const [userOrganizationsError, setUserOrganizationsError] = useState<string | null>(null);
  const [userOrganizationsLoading, setUserOrganizationsLoading] = useState(true);
  
  useEffect(() => {
    loadCourses();
    loadStats();
    loadUserOrganizations();
  }, []);
  
  useEffect(() => {
    if (courses.length > 0) {
      filterCourses();
    }
  }, [searchTerm, showDrafts, showPublished, courses]);
  
  const loadUserOrganizations = async () => {
    setUserOrganizationsLoading(true);
    setUserOrganizationsError(null);
    
    try {
      // Use the organizationService to fetch user organizations
      const orgData = await organizationService.getOrganizations();
      
      // Check if the response is an array or HTML (error case)
      if (!orgData || !Array.isArray(orgData)) {
        console.error('Invalid response from organization service:', 
          typeof orgData === 'string' ? orgData.substring(0, 100) + '...' : orgData);
        
        if (typeof orgData === 'string' && orgData.includes('<!DOCTYPE html>')) {
          throw new Error('Received HTML instead of JSON data - API routing issue');
        }
        
        setUserOrganizations([]);
        setUserOrganizationsError('Failed to load organization data (invalid format)');
        return;
      }
      
      // Transform the data to match the expected format
      const transformedOrgs = orgData.map(org => ({
        id: org.id,
        role: org.role || 'member',
        organizations: {
          id: org.id,
          name: org.name,
          description: org.description
        }
      }));
      
      setUserOrganizations(transformedOrgs);
    } catch (error) {
      console.error('Error loading user organizations:', error);
      
      // More descriptive error message
      const errorMessage = error instanceof Error
        ? `Failed to load organizations: ${error.message}`
        : 'Failed to load organizations data. Please try again later.';
        
      setUserOrganizationsError(errorMessage);
      setUserOrganizations([]);
    } finally {
      setUserOrganizationsLoading(false);
    }
  };
  
  const loadCourses = async () => {
    setIsLoading(true);
    try {
      const coursesResponse = await fetchCourses();
      
      // Log the response for debugging
      console.log('fetchCourses response:', coursesResponse);
      
      // Handle the different possible response formats
      let coursesData = [];
      
      if (coursesResponse) {
        if (Array.isArray(coursesResponse)) {
          // Handle case where API returns an array directly
          coursesData = coursesResponse;
        } else if (coursesResponse.courses && Array.isArray(coursesResponse.courses)) {
          // Handle case where API returns { courses: [...], totalCount: n }
          coursesData = coursesResponse.courses;
        } else if (typeof coursesResponse === 'object') {
          // If it's a single course object, wrap in array
          coursesData = [coursesResponse];
        }
      }
      
      console.log('Processed courses data:', coursesData);
      
      if (!coursesData.length) {
        setCourses([]);
        setFilteredCourses([]);
        setIsLoading(false);
        return;
      }
      
      const coursesWithDetails = await Promise.all(coursesData.map(async (course) => {
        try {
          const lessons = await fetchLessonsByCourseId(course.id);
          
          return {
            id: course.id,
            title: course.title,
            description: course.description || null,
            // Use the status field directly, or fall back to isPublished for compatibility
            status: course.status || (course.isPublished ? 'published' as const : 'draft' as const),
            lesson_count: lessons ? lessons.length : 0,
            created_at: course.createdAt || new Date().toISOString(),
            updated_at: course.updatedAt || new Date().toISOString(),
            created_at_formatted: formatDate(course.createdAt),
            updated_at_formatted: formatDate(course.updatedAt)
          } as CourseData;
        } catch (err) {
          console.error(`Error processing course ${course.id}:`, err);
          return null;
        }
      }));
      
      // Filter out any null entries from errors
      const validCourses = coursesWithDetails.filter(Boolean) as CourseData[];
      
      setCourses(validCourses);
      setFilteredCourses(validCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast({
        title: "Failed to load courses",
        description: "There was an error loading the courses. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadStats = async () => {
    try {
      // Fetch dashboard stats from the API
      const dashboardStats = await fetchDashboardStats();
      
      if (dashboardStats) {
        // Get courses to calculate published/draft counts
        const coursesResponse = await fetchCourses();
        
        // Handle the different possible response formats
        let coursesData = [];
        
        if (coursesResponse) {
          if (Array.isArray(coursesResponse)) {
            coursesData = coursesResponse;
          } else if (coursesResponse.courses && Array.isArray(coursesResponse.courses)) {
            coursesData = coursesResponse.courses;
          } else if (typeof coursesResponse === 'object') {
            coursesData = [coursesResponse];
          }
        }
        
        // Calculate published/draft counts
        const published = coursesData.filter(c => c.status === 'published' || c.isPublished).length;
        const drafts = coursesData.filter(c => c.status === 'draft' || (!c.isPublished && !c.status)).length;
        
        // Set stats with values from the API and calculated values
        setStats({
          totalCourses: dashboardStats.totalCourses,
          publishedCourses: published,
          draftCourses: drafts,
          totalUsers: dashboardStats.totalUsers,
          activeUsers: dashboardStats.totalUsers || 0, // We don't have this info yet
          completedCourses: dashboardStats.totalCompleted,
          totalOrganizations: dashboardStats.totalOrganizations
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      // Set default stats if API call fails
      setStats({
        totalCourses: 0,
        publishedCourses: 0,
        draftCourses: 0,
        totalUsers: 0,
        activeUsers: 0,
        completedCourses: 0,
        totalOrganizations: 0
      });
    }
  };
  
  const filterCourses = () => {
    let filtered = [...courses];
    
    if (!showDrafts && !showPublished) {
      filtered = [];
    } else if (!showDrafts) {
      filtered = filtered.filter(course => course.status === 'published');
    } else if (!showPublished) {
      filtered = filtered.filter(course => course.status === 'draft');
    }
    
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(searchLower) ||
        (course.description && course.description.toLowerCase().includes(searchLower))
      );
    }
    
    setFilteredCourses(filtered);
  };

  return {
    courses,
    filteredCourses,
    isLoading,
    stats,
    userOrganizations,
    userOrganizationsError,
    userOrganizationsLoading,
    searchTerm,
    setSearchTerm,
    showDrafts,
    setShowDrafts,
    showPublished,
    setShowPublished,
    loadCourses,
    loadUserOrganizations
  };
};

export const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  if (dateString.includes('/')) return dateString;
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString();
};
